import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { PPUIGrid } from 'pricing-pages-ui-components/grid/PPUIGrid';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { useSelector } from 'react-redux';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import SuiteProductLevelIdentifiersMap from '../../constants/merchCards/SuiteProductLevelIdentifiersMap';
import { SUITE_CARD } from '../../constants/tracking/TrackingSources';
import { SuiteCTA } from '../../features/purchaseMotions/components/SuiteCTA';
import { TalkToSalesFallback } from '../../features/purchaseMotions/components/TalkToSalesFallback';
import useSuitePurchaseMotions from '../../features/purchaseMotions/hooks/useSuitePurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import { useFTS0011ExperimentAndLogExposure } from '../../utils/experiments/fts0011';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import { SuiteBadge } from '../common/badges/SuiteBadge';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../error-boundary/ErrorBoundary';
import IncludedSeatsDisplay from './IncludedSeatsDisplay';
import { MerchCardWrapper } from './MerchCardSharedComponents';
import SuiteTermSelectorContainer from './SuiteTermSelectorContainer';
import { SuiteFeatures } from './features-display/SuiteFeatures';
import CrmSuitePriceMerchCard from './price-display/CrmSuitePriceMerchCard';
import { useCrmSuiteBundlePricingMetadata } from './price-display/useCrmSuitePricingInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getStringKeysForProductLevel = productLevel => {
  switch (productLevel) {
    case FREE:
      return {
        titleKey: 'merchCards.suiteTile.freeCard.title',
        subtitleKey: 'merchCards.suiteTile.freeCard.description'
      };
    case STARTER:
      return {
        titleKey: 'merchCards.suiteTile.starterCard.title',
        subtitleKey: 'merchCards.suiteTile.starterCard.description'
      };
    case PROFESSIONAL:
      return {
        titleKey: 'merchCards.suiteTile.professionalCard.title',
        subtitleKey: 'merchCards.suiteTile.professionalCard.description'
      };
    case ENTERPRISE:
      return {
        titleKey: 'merchCards.suiteTile.enterpriseCard.title',
        subtitleKey: 'merchCards.suiteTile.enterpriseCard.description'
      };
    default:
      return {
        titleKey: '',
        subtitleKey: ''
      };
  }
};
// This is used as the merch card for the customer platform routes
export const SuiteTile = ({
  productLevel,
  screen,
  gridItemProps
}) => {
  const isAppReady = useGlobalLoading();
  const ownsProductVersion = useSelector(state => ownsCrmSuiteProductLevel(state, {
    productLevel
  }));
  const {
    showPricePerMonthPerSeat,
    suiteSkuId
  } = useCrmSuiteBundlePricingMetadata({
    productLevel
  });
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const {
    titleKey,
    subtitleKey
  } = getStringKeysForProductLevel(productLevel);
  const purchaseMotions = useSuitePurchaseMotions({
    productLevel
  });
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  const {
    isFTS0011Variant
  } = useFTS0011ExperimentAndLogExposure();
  return /*#__PURE__*/_jsxs(MerchCardWrapper, {
    children: [/*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      "data-selenium-id": `${SuiteProductLevelIdentifiersMap[productLevel]}-merch-card`,
      paddingBlockStart: "800",
      position: "relative",
      gridRowStart: 1,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(SuiteBadge, {
          productLevel: productLevel
        }), /*#__PURE__*/_jsx(Flex, {
          alignItems: "center",
          children: /*#__PURE__*/_jsx(SectionTitle, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: titleKey
            })
          })
        })]
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "0",
      gridRowStart: 2,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: subtitleKey
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 3,
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady
          // First block is for "Starts at"
          // Second block is for the price
          ,
          heights: [20, 33],
          widths: [60, 80],
          children: /*#__PURE__*/_jsx(CrmSuitePriceMerchCard, {
            productLevel: productLevel,
            showPricePerMonthPerSeat: showPricePerMonthPerSeat
          })
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 4,
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
          isLoading: !isAppReady,
          heights: [20],
          widths: [140],
          children: suiteSkuId ? /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            gap: "400",
            children: /*#__PURE__*/_jsx(IncludedSeatsDisplay, {
              skuId: suiteSkuId
            })
          }) : null
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 5,
      children: !ownsProductVersion && /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [41],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(SuiteTermSelectorContainer, {
          productLevel: productLevel,
          onChange: setQueryParamTerm,
          queryParamTerm: queryParamTerm,
          trackingSource: SUITE_CARD
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 7,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [47],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: suiteSkuId ? /*#__PURE__*/_jsx(TalkToSalesFallback, {
            skuId: suiteSkuId,
            screen: screen,
            trackingSource: SUITE_CARD
          }) : null,
          children: /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            justifyContent: "center",
            gap: "200",
            inlineSize: "100%",
            children: [purchaseMotions.map((purchaseMotion, index) => {
              return /*#__PURE__*/_jsx("div", {
                children: /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
                  use: index === 0 ? 'primary' : 'secondary',
                  purchaseMotion: purchaseMotion,
                  productLevel: productLevel,
                  trackingSource: SUITE_CARD,
                  screen: screen,
                  purchaseMotionLocation: 'MERCH_CARD'
                }, ctaVariantProps))
              }, purchaseMotion);
            }), isFTS0011Variant && productLevel === STARTER && /*#__PURE__*/_jsx(Text, {
              typography: "detail-100",
              textAlign: "center",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "merchCards.starterCustomerPlatformUpsellCard.noCreditCardRequired"
              })
            })]
          })
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGrid, Object.assign({}, gridItemProps, {
      gridTemplateRows: "subgrid",
      gridRowStart: 8,
      gridRowEnd: 10,
      paddingBlockStart: "800",
      children: /*#__PURE__*/_jsx(SuiteFeatures, {
        productLevel: productLevel,
        loading: !isAppReady
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, {
      gridRowStart: 11,
      paddingBlockStart: "400",
      paddingBlockEnd: "600"
    })]
  });
};